import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ChakraProvider } from '@chakra-ui/react';

// Context
import { AuthProvider } from './contexts/AuthContext';

// Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Login from './components/pages/Authentication/Login';
import SignUp from './components/pages/Authentication/SignUp';
import Home from './components/pages/Generic/Home';
import Sites from './components/pages/Sites/Sites';
import EditSite from './components/pages/Sites/EditSite';
import CreateSite from './components/pages/Sites/CreateSite';
import ContentTypes from './components/pages/ContentTypes/ContentTypes';
import ContentModel from './components/pages/ContentTypes/ContentModels/ContentModel';
import EditContentModel from './components/pages/ContentTypes/ContentModels/EditContentModel';
import CreateContentModel from './components/pages/ContentTypes/ContentModels/CreateContentModel';
import Content from './components/pages/ContentEntries/Content';
import EditContent from './components/pages/ContentEntries/EditContent';
import CreateContent from './components/pages/ContentEntries/CreateContent';
import Media from './components/pages/Media/Media';
import EditMedia from './components/pages/Media/EditMedia';
import CreateMedia from './components/pages/Media/CreateMedia';
import Settings from './components/pages/Settings/Settings';
import TaxonomyList from './components/pages/ContentTypes/Taxonomies/TaxonomyList';
import TaxonomyCreate from './components/pages/ContentTypes/Taxonomies/TaxonomyCreate';
import TaxonomyEdit from './components/pages/ContentTypes/Taxonomies/TaxonomyEdit';

// Fixed Profile import path
import Profile from './components/pages/Users/Profile';

import Help from './components/pages/Help/Help';
import Documentation from './components/pages/Documentation/Documentation';
import UserGuide from './components/pages/Documentation/UserGuide';

// New pages for the CMS features
import Pages from './components/pages/Pages/Pages';
import CreatePage from './components/pages/Pages/CreatePage';
import EditPage from './components/pages/Pages/EditPage';
import PageBuilder from './components/pages/Pages/PageBuilder';
import PagePreview from './components/pages/Pages/PagePreview';


// Template pages
import Templates from './components/pages/Templates/Templates';
import CreateTemplate from './components/pages/Templates/CreateTemplate';
import EditTemplate from './components/pages/Templates/EditTemplate';
import ViewTemplate from './components/pages/Templates/ViewTemplate';

import SitesNavigation from './components/pages/SitesNavigation/SitesNavigation';
import CreateNavigation from './components/pages/SitesNavigation/CreateNavigation';
import EditNavigation from './components/pages/SitesNavigation/EditNavigation';
import ABTesting from './components/pages/ABTesting/ABTesting';
import CreateABTest from './components/pages/ABTesting/CreateABTest';
import EditABTest from './components/pages/ABTesting/EditABTest';
import ABTestResults from './components/pages/ABTesting/ABTestResults';
import Scheduling from './components/pages/Scheduling/Scheduling';
import CreateSchedule from './components/pages/Scheduling/CreateSchedule';
import EditSchedule from './components/pages/Scheduling/EditSchedule';

// Component and Layout Libraries
import ComponentLibrary from './components/pages/ComponentLibrary/ComponentLibrary';
import ComponentForm from './components/pages/ComponentLibrary/ComponentForm';
import LayoutLibrary from './components/pages/LayoutLibrary/LayoutLibrary';
import LayoutForm from './components/pages/LayoutLibrary/LayoutForm';

function App() {
  return (
    <ChakraProvider>
      <Router basename="/dashboard">
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      
                      {/* Sites */}
                      <Route path="/sites" element={<Sites />} />
                      <Route path="/sites/edit/:id" element={<EditSite />} />
                      <Route path="/sites/create" element={<CreateSite />} />
                      
                      {/* Content Types */}
                      <Route path="/content-types" element={<ContentTypes />} />
                      <Route path="/content-models" element={<ContentModel />} />
                      <Route path="/content-models/edit/:id" element={<EditContentModel />} />
                      <Route path="/content-models/create" element={<CreateContentModel />} />
                      
                      {/* Content Entries */}
                      <Route path="/content" element={<Content />} />
                      <Route path="/content/edit/:id" element={<EditContent />} />
                      <Route path="/content/create" element={<CreateContent />} />
                      
                      {/* Pages */}
                      <Route path="/pages" element={<Pages />} />
                      <Route path="/pages/create" element={<CreatePage />} />
                      <Route path="/pages/edit/:id" element={<EditPage />} />
                      <Route path="/pages/:pageId/builder" element={<PageBuilder />} />
                      <Route path="/pages/:pageId/preview" element={<PagePreview />} />
                      
                      
                      {/* Templates */}
                      <Route path="/templates" element={<Templates />} />
                      <Route path="/templates/create" element={<CreateTemplate />} />
                      <Route path="/templates/:id" element={<ViewTemplate />} />
                      <Route path="/templates/:id/edit" element={<EditTemplate />} />
                      
                      {/* Navigation */}
                      <Route path="/navigation" element={<SitesNavigation />} />
                      <Route path="/navigation/create" element={<CreateNavigation />} />
                      <Route path="/navigation/edit/:id" element={<EditNavigation />} />
                      
                      {/* A/B Testing */}
                      <Route path="/ab-testing" element={<ABTesting />} />
                      <Route path="/ab-testing/create" element={<CreateABTest />} />
                      <Route path="/ab-testing/edit/:id" element={<EditABTest />} />
                      <Route path="/ab-testing/results/:id" element={<ABTestResults />} />
                      
                      {/* Scheduling */}
                      <Route path="/scheduling" element={<Scheduling />} />
                      <Route path="/scheduling/create" element={<CreateSchedule />} />
                      <Route path="/scheduling/edit/:id" element={<EditSchedule />} />
                      
                      {/* Media */}
                      <Route path="/media" element={<Media />} />
                      <Route path="/media/edit/:id" element={<EditMedia />} />
                      <Route path="/media/create" element={<CreateMedia />} />
                      
                      {/* Taxonomies */}
                      <Route path="/taxonomies" element={<TaxonomyList />} />
                      <Route path="/taxonomies/create" element={<TaxonomyCreate />} />
                      <Route path="/taxonomies/edit/:id" element={<TaxonomyEdit />} />
                      
                      {/* Settings */}
                      <Route path="/settings/*" element={<Settings />} />
                      
                      {/* Documentation */}
                      <Route path="/user-guide" element={<UserGuide />} />
                      <Route path="/documentation" element={<Documentation />} />
                      
                      {/* Other */}
                      <Route path="/profile" element={<Profile />} />
                      <Route path="/help" element={<Help />} />
                      
                      {/* Component Library Routes */}
                      <Route path="/component-library" element={<ComponentLibrary />} />
                      <Route path="/component-library/create" element={<ComponentForm />} />
                      <Route path="/component-library/edit/:id" element={<ComponentForm />} />
                      
                      {/* Layout Library Routes */}
                      <Route path="/layout-library" element={<LayoutLibrary />} />
                      <Route path="/layout-library/create" element={<LayoutForm />} />
                      <Route path="/layout-library/edit/:id" element={<LayoutForm />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </Router>
    </ChakraProvider>
  );
}

export default App;